import { useEffect, useState } from "react";
import {
  isArchiveMediaType,
  baseMediaType,
  isCsvMediaType,
  isSafeInlineMediaType,
  isSqliteMediaType,
} from "../../../shared/attachmentPolicy";
import { officePreviewFormat } from "../../lib/officePreview";

const MAX_INLINE_BYTES = 5 * 1024 * 1024;
const MAX_ISOLATED_PREVIEW_BYTES = 25 * 1024 * 1024;
export const PREVIEW_BYTES = 64 * 1024;

export type UrlContent = {
  loading: boolean;
  failed: boolean;
  contentType: string | undefined;
  content: string | undefined;
  blob: Blob | undefined;
  size: number | undefined;
  truncated: boolean;
};

const empty: UrlContent = {
  loading: false,
  failed: false,
  contentType: undefined,
  content: undefined,
  blob: undefined,
  size: undefined,
  truncated: false,
};

// CSV and SQLite have dedicated viewers that read from `url` themselves, so we
// don't read their bytes inline here even though CSV is technically text.
function isTextLike(contentType: string): boolean {
  if (contentType === "text/csv") return false;
  return (
    contentType.startsWith("text/") ||
    contentType === "application/json" ||
    contentType === "application/jsonl"
  );
}

function usesAppRenderer(contentType: string, fileName?: string): boolean {
  return (
    isTextLike(contentType) ||
    isSafeInlineMediaType(contentType) ||
    contentType === "application/pdf" ||
    isCsvMediaType(contentType) ||
    isSqliteMediaType(contentType) ||
    isArchiveMediaType(contentType) ||
    contentType === "message/http" ||
    contentType === "message/rfc822" ||
    contentType === "application/octet-stream" ||
    contentType === "application/msword" ||
    contentType === "application/vnd.ms-excel" ||
    contentType === "application/vnd.ms-powerpoint" ||
    officePreviewFormat(contentType, fileName) != null
  );
}

async function readIsolatedPreviewBlob(
  response: Response,
  contentType: string
): Promise<Blob | null> {
  if (
    Number(response.headers.get("Content-Length")) > MAX_ISOLATED_PREVIEW_BYTES
  ) {
    await response.body?.cancel();
    return null;
  }
  if (!response.body) return new Blob([], { type: contentType });

  const reader = response.body.getReader();
  const chunks: Uint8Array<ArrayBuffer>[] = [];
  let size = 0;
  try {
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      size += value.byteLength;
      if (size > MAX_ISOLATED_PREVIEW_BYTES) {
        await reader.cancel();
        return null;
      }
      chunks.push(value);
    }
  } finally {
    reader.releaseLock();
  }
  return new Blob(chunks, { type: contentType });
}

export function useUrlContent(
  url: string,
  fileName?: string
): UrlContent & { reload: () => void } {
  const [result, setResult] = useState<UrlContent>(empty);
  const [revision, setRevision] = useState(0);

  useEffect(() => {
    const controller = new AbortController();
    const { signal } = controller;
    // oxlint-disable-next-line react/set-state-in-effect -- EffectSetState: resets to loading at the top of the AbortController content fetch
    setResult({ ...empty, loading: true });

    void fetch(url, { credentials: "include", signal })
      .then(async (res) => {
        if (!res.ok) {
          setResult({ ...empty, failed: true });
          return;
        }
        const contentType = baseMediaType(
          res.headers.get("Content-Type") ?? "text/plain"
        );
        const size = Number(res.headers.get("Content-Length")) || 0;

        if (!usesAppRenderer(contentType, fileName)) {
          const blob = await readIsolatedPreviewBlob(res, contentType);
          if (signal.aborted) return;
          setResult({
            ...empty,
            contentType,
            blob: blob ?? undefined,
            size: blob?.size || size,
          });
          return;
        }

        if (!isTextLike(contentType)) {
          res.body?.cancel();
          setResult({ ...empty, contentType, size });
          return;
        }

        const blob = await res.blob();
        if (signal.aborted) return;
        const truncated = blob.size > MAX_INLINE_BYTES;
        const content = await (
          truncated ? blob.slice(0, PREVIEW_BYTES) : blob
        ).text();
        if (signal.aborted) return;
        setResult({
          loading: false,
          failed: false,
          contentType,
          content,
          blob: undefined,
          size: blob.size || size,
          truncated,
        });
      })
      .catch(() => {
        if (!signal.aborted) setResult({ ...empty, failed: true });
      });

    return () => controller.abort();
  }, [url, fileName, revision]);

  return { ...result, reload: () => setRevision((previous) => previous + 1) };
}
